import * as React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTask } from '@/modules/shared/context/TaskContext'
import { TaskType } from '@/app/constants/taskType'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import {
  isTaskRateLimited,
  startTrackedTask,
} from '@/modules/shared/utils/startTrackedTask'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import { useScopeBusy } from '@/modules/shared/hooks/useScopeBusy'
import { hasOutOfDateQuarterRateNotice } from './useOutOfDateRateConfirm'
import { notifyTaxExemptNotices } from './taxExemptNotice'

import axios from 'axios'
import { YearMonthsValue } from '@/modules/drivers/types/date'

interface UseGroupHandlersProps {
  companyId: string
  selectedMonthKey: string
  driverIds: string[]
  period: YearMonthsValue
  /**
   * Pytanie o kierowcow z aktywnosciami zagranicznymi bez delegacji. Opcjonalne -
   * ekrany pojedynczego kierowcy nie maja tego dialogu i wtedy zostaje sam dymek.
   * `false` przerywa przeliczenie.
   */
  confirmForeignActivityWithoutTrip?: (driverNames: string) => Promise<boolean>
  /**
   * Pytanie o liczenie po nieaktualnym kursie kwartalnym. Bez tego callbacku brak
   * kursu przerywa przeliczenie - cichej zgody nie zakladamy (parytet z WPF).
   */
  confirmOutOfDateExchangeRate?: () => Promise<boolean>
}

const translateErrorItem = (
  errorItem: any,
  t: (key: string) => string,
): string => {
  const errorCode = errorItem?.code || ''

  if (errorCode === 'TIMESHEET_CONTRACT_NOT_FOUND' && errorItem?.message) {
    const driverFullName = errorItem.message
    return `"${driverFullName}" ${t('timesheet.no-contract-error')}`
  }

  const translatedError = t(`errorCode.${errorCode}`)

  if (translatedError && translatedError !== `errorCode.${errorCode}`) {
    return translatedError
  }

  return errorItem?.message || errorCode || t('errorCodes.general-error')
}

const displayAxiosError = (
  err: any,
  t: (key: string) => string,
  title: string,
) => {
  if (axios.isAxiosError(err)) {
    const { data } = err.response

    if (Array.isArray(data) && data.length > 0) {
      data.forEach((errorItem) => {
        const errorMessage = translateErrorItem(errorItem, t)
        if (errorMessage) {
          toast.error({
            title,
            text: errorMessage,
          })
        }
      })
      return
    }

    if (data && typeof data === 'object') {
      const errorMessage = translateErrorItem(data, t)
      toast.error({
        title,
        text: errorMessage || t('errorCodes.general-error'),
      })
      return
    }

    toast.error({
      title,
      text: t('errorCodes.general-error'),
    })
    return
  }

  const errorMessage = err instanceof Error ? err.message : String(err)
  toast.error({
    title,
    text: errorMessage,
  })
}

export const useGroupCalculateHandlers = ({
  period,
  companyId,
  selectedMonthKey,
  driverIds,
  confirmForeignActivityWithoutTrip,
  confirmOutOfDateExchangeRate,
}: UseGroupHandlersProps) => {
  const { t } = useLocalize()
  const { addTask } = useTask()
  const { precalculateTimesheet, calculateTimesheetLegacy } =
    useTimesheetService()

  // The scope this button would occupy. Computed the same way as on the server, so
  // "already running" is visible here before the request is even sent — including a
  // run started by a different user of the same company, which the client had no way
  // of knowing about at all.
  const scopeKey =
    companyId && period?.year && period?.months?.length
      ? taskScopeKey.timesheet(companyId, period.year, period.months)
      : null
  const scopeBusy = useScopeBusy(scopeKey)

  const handleCalculate = React.useCallback(async (): Promise<boolean> => {
    try {
      // Somebody else is already recalculating this period. Starting a second run
      // would be refused by the server anyway, so we say so instead of sending a
      // request whose only possible answer is "already running".
      //
      // The wording has to admit that this selection is NOT queued: the scope key is
      // per period, not per driver set, so the running task may well cover different
      // drivers than the ones just picked. Saying only "already running" let the user
      // walk away believing their drivers were included.
      if (scopeBusy.busy && scopeBusy.byOther) {
        toast.info({
          title: t('timesheet.calculation-in-progress'),
          text: scopeBusy.startedBy
            ? `${scopeBusy.startedBy}: ${t('timesheet.calculation-adopted-other-selection')}`
            : t('timesheet.calculation-adopted-other-selection'),
        })
        return false
      }

      if (!companyId) {
        toast.error({
          title: t('timesheet.calculated-error'),
          text: t('timesheet.missing-company-id'),
        })
        return false
      }

      if (
        !period ||
        !period.year ||
        !period.months ||
        period.months.length === 0
      ) {
        toast.error({
          title: t('timesheet.calculated-error'),
          text: t('timesheet.TIMESHEET_PERIOD_NOT_SELECTED'),
        })
        return false
      }

      // Validate drivers
      if (!driverIds || driverIds.length === 0) {
        toast.error({
          title: t('timesheet.calculated-error'),
          text: t('timesheet.no-drivers-selected'),
        })
        return false
      }

      const driverNumbers = driverIds
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id) && id > 0)

      if (driverNumbers.length === 0) {
        toast.error({
          title: t('timesheet.calculated-error'),
          text: t('timesheet.invalid-driver-ids'),
        })
        return false
      }

      const requestId = uuidv4()

      // Pierwszy przebieg BEZ zgody na starszy kurs - przy `true` serwer w ogole nie
      // zglosi, ze kursu na ten kwartal nie ma. Po zgodzie powtarzamy caly precalculate,
      // zeby nie pominac reszty walidacji. Blad powtorki obsluguje catch calej funkcji.
      const runPrecalculate = (accepted: boolean) =>
        precalculateTimesheet({
          companyId,
          drivers: driverNumbers,
          months: period.months,
          year: period.year,
          outOfDateQuarterExchangeRateAccepted: accepted,
          id: requestId,
        })

      let outOfDateAccepted = false
      let precalculateResult
      try {
        precalculateResult = await runPrecalculate(false)
      } catch (precalculateError) {
        if (
          !confirmOutOfDateExchangeRate ||
          !hasOutOfDateQuarterRateNotice(precalculateError)
        ) {
          console.error('Error during precalculate:', precalculateError)
          displayAxiosError(precalculateError, t, t('timesheet.calculated-error'))
          return false
        }

        if (!(await confirmOutOfDateExchangeRate())) return false
        outOfDateAccepted = true
        precalculateResult = await runPrecalculate(true)
      }


      // Kierowcy z aktywnosciami zagranicznymi bez delegacji. Serwer zwraca ich jako
      // Success z wlasnym kodem - kazdy inny typ dalby 400 i wyladowal w catch wyzej.
      // Pytamy TU, miedzy precalculate a przeliczeniem, bo dopiero teraz to wiemy.
      const foreignActivityNames = (
        Array.isArray(precalculateResult) ? precalculateResult : []
      )
        .filter(
          (notice: any) =>
            notice?.code === 'TIMESHEET_FOREIGN_ACTIVITY_WITHOUT_TRIP' &&
            notice?.message,
        )
        // Rekordy rozdziela nowa linia, nie przecinek - nazwiska i listy krajow
        // same zawieraja przecinki.
        .map((notice: any) => String(notice.message))
        .join('\n')

      if (foreignActivityNames) {
        if (confirmForeignActivityWithoutTrip) {
          const confirmed =
            await confirmForeignActivityWithoutTrip(foreignActivityNames)
          if (!confirmed) {
            return false
          }
        } else {
          // Bez dialogu (ekrany pojedynczego kierowcy) zostaje dymek - z samymi
          // nazwiskami, bo szczegoly sa w formacie "nazwisko|dni|zakres|kraje".
          const names = foreignActivityNames
            .split(/\r?\n/)
            .map((line) => line.split('|')[0].trim())
            .filter((name) => name !== '')
            .join(', ')
          toast.warning({
            title: t('timesheet.force-calculation-title-warning'),
            text: `"${names}" ${t('timesheet.foreign-activity-without-trip')}`,
          })
        }
      }

      notifyTaxExemptNotices(precalculateResult, t)

      if (precalculateResult?.type === 'ERROR') {
        console.error(
          'Precalculate validation error:',
          precalculateResult.error,
        )

        if (Array.isArray(precalculateResult.error)) {
          precalculateResult.error.forEach((e: any) => {
            const errorMessage = translateErrorItem(e, t)
            if (errorMessage) {
              toast.error({
                title: t('timesheet.force-calculation-title-error'),
                text: errorMessage,
              })
            }
          })
        } else {
          const errorMessage = translateErrorItem(precalculateResult.error, t)
          toast.error({
            title: t('timesheet.force-calculation-title-error'),
            text: errorMessage || t('timesheet.force-calculation-error'),
          })
        }
        return false
      }

      if (precalculateResult?.type === 'WARNING' && precalculateResult.error) {
        console.warn('Precalculate warnings:', precalculateResult.error)

        // Display each warning in a separate toast
        if (Array.isArray(precalculateResult.error)) {
          precalculateResult.error.forEach((e: any) => {
            const warningMessage = translateErrorItem(e, t)
            if (warningMessage) {
              toast.warning({
                title: t('timesheet.force-calculation-title-warning'),
                text: warningMessage,
              })
            }
          })
        } else {
          const warningMessage = translateErrorItem(precalculateResult.error, t)
          toast.warning({
            title: t('timesheet.force-calculation-title-warning'),
            text: warningMessage || t('timesheet.force-calculation-warning'),
          })
        }
      }

   
      const bonusConfigurations: Array<any> = []

      try {
        // "Already running" is a normal answer here, not a failure: a colleague may
        // have started the same month, or this user refreshed and clicked again.
        // startTrackedTask adopts the running task so they watch the work they asked
        // for instead of getting an error and queueing a duplicate.
        const { adopted } = await startTrackedTask({
          companyId,
          type: TaskType.Timesheet,
          taskId: requestId,
          scopeKey: taskScopeKey.timesheet(companyId, period.year, period.months),
          start: () =>
            calculateTimesheetLegacy({
              companyId,
              drivers: driverNumbers,
              months: period.months,
              year: period.year,
              outOfDateQuarterExchangeRateAccepted: outOfDateAccepted,
              bonusConfigurations,
              id: requestId,
            }),
        })

        if (adopted) {
          toast.info({
            title: t('timesheet.calculation-in-progress'),
            text: t('timesheet.calculation-adopted-other-selection'),
          })
        }
        return true
      } catch (calculateError) {
        if (isTaskRateLimited(calculateError)) {
          toast.warning({
            title: t('timesheet.calculation-in-progress'),
            text: t('timesheet.too-many-tasks'),
          })
          return false
        }
        console.error('Error calculating timesheet:', calculateError)
        displayAxiosError(calculateError, t, t('timesheet.calculated-error'))
        return false
      }
    } catch (error) {
      console.error('Unexpected error in handleCalculate:', error)
      displayAxiosError(error, t, t('timesheet.calculated-error'))
      return false
    }
  }, [
    driverIds,
    selectedMonthKey,
    period,
    companyId,
    precalculateTimesheet,
    calculateTimesheetLegacy,
    addTask,
    t,
    confirmForeignActivityWithoutTrip,
    confirmOutOfDateExchangeRate,
    scopeBusy.busy,
    scopeBusy.byOther,
    scopeBusy.startedBy,
    scopeKey,
  ])

  return {
    handleCalculate,
    /** Lets callers disable the button and explain who is holding the period. */
    scopeBusy,
  }
}
